import { useNavigate } from 'react-router-dom';
import { Organigrama } from './Organigrama';
import { Grid3X3, LogOut, UserCircle, Building2, Briefcase, Users, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OrganigramaViewer() {
  const navigate = useNavigate();
  const { logout, token } = useAuth();
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));
  const [empresaOrg, setEmpresaOrg] = useState(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(true);

  useEffect(() => {
    if (!logoImage) {
      axios.get(`${API}/configuracion`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.logo_image) {
            let url = res.data.logo_image;
            if (!url.startsWith('http') && !url.startsWith('data:')) url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
            setLogoImage(url);
            localStorage.setItem('cached_logo_url', url);
          }
        }).catch(() => {});
    }
    // Detectar si el trabajador es de empresa contratista
    axios.get(`${API}/trabajador/mi-empresa-organigrama`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setEmpresaOrg(res.data?.empresa ? res.data : null))
      .catch(() => setEmpresaOrg(null))
      .finally(() => setLoadingEmpresa(false));
  }, [token, logoImage]);

  const esContratista = !!empresaOrg?.empresa;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="organigrama-viewer">
      <header className="px-5 py-3 flex items-center justify-between shrink-0" style={{ backgroundColor: esContratista ? '#0F766E' : '#00827F' }}>
        <div className="flex items-center gap-4">
          {logoImage && (
            <img src={logoImage} alt="Logo" className="h-12 object-contain" style={{ filter: 'brightness(0) invert(1)' }}
              onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }} />
          )}
          <div>
            <h1 className="text-base font-bold text-white tracking-wide">
              {esContratista ? `Organigrama · ${empresaOrg.empresa.razon_social}` : 'Organigrama'}
            </h1>
            <p className="text-xs text-white/70">{esContratista ? 'Mi empresa colaboradora' : 'Estructura organizacional'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/perfil-viewer')}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="volver-perfil-btn">
            <UserCircle className="w-3.5 h-3.5" /> Volver a Mi Perfil
          </button>
          <button onClick={() => navigate('/seleccionar-rol')}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="volver-tysta-btn">
            <Grid3X3 className="w-3.5 h-3.5" /> TystaCore
          </button>
          <button onClick={() => { logout(); navigate('/login'); }}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Salir
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-4">
        {loadingEmpresa ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          </div>
        ) : esContratista ? (
          <EmpresaOrgTree data={empresaOrg} />
        ) : (
          <Organigrama hideCapacitaciones defaultZoom={90} />
        )}
      </main>
      <footer className="py-2 text-center shrink-0"><p className="text-xs text-slate-400">&copy; TystaCore {new Date().getFullYear()}</p></footer>
    </div>
  );
}

/* ============== Vista de organigrama de empresa contratista ============== */
function EmpresaOrgTree({ data }) {
  const { empresa, cargos = [], personas = [] } = data;

  // Construye estructura tipo árbol
  const rootCargos = cargos.filter(c => !c.cargo_superior_id);
  const childrenOf = (id) => cargos.filter(c => c.cargo_superior_id === id);
  const personasOfCargo = (cargoId) => personas.filter(p => p.cargo_id === cargoId);
  const sinCargo = personas.filter(p => !p.cargo_id);

  if (cargos.length === 0 && personas.length === 0) {
    return (
      <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center max-w-2xl mx-auto">
        <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-base font-semibold text-slate-700">{empresa.razon_social}</p>
        <p className="text-sm text-slate-500 mt-1">Aún no se ha cargado el organigrama de tu empresa.</p>
        <p className="text-xs text-slate-400 mt-2">Tu Key User puede crear los cargos y asignar personas desde su perfil.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="empresa-org-tree">
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white rounded-2xl p-5 mb-5 shadow">
        <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Mi empresa colaboradora</p>
        <h2 className="text-2xl font-bold mt-1 flex items-center gap-2">
          <Building2 className="w-6 h-6" />
          {empresa.razon_social}
        </h2>
        <p className="text-xs opacity-80 mt-0.5 font-mono">{empresa.rut}</p>
        <div className="flex gap-4 mt-3 text-xs">
          <span className="bg-white/15 px-2.5 py-1 rounded-full">
            <Briefcase className="w-3 h-3 inline mr-1" /> {cargos.length} cargo{cargos.length !== 1 ? 's' : ''}
          </span>
          <span className="bg-white/15 px-2.5 py-1 rounded-full">
            <Users className="w-3 h-3 inline mr-1" /> {personas.length} persona{personas.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {rootCargos.length > 0 ? (
        <div className="space-y-3">
          {rootCargos.map(c => (
            <CargoNode key={c.id} cargo={c} childrenOf={childrenOf} personasOfCargo={personasOfCargo} />
          ))}
        </div>
      ) : (
        <div className="text-center text-sm text-slate-500 mb-4">
          (No hay cargos raíz. Lista plana abajo)
        </div>
      )}

      {sinCargo.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wide">Personas sin cargo asignado</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {sinCargo.map(p => (
              <div key={p.id} className="px-4 py-2 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{p.nombre} {p.apellido}</p>
                  <p className="text-[10px] font-mono text-slate-500">{p.rut}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CargoNode({ cargo, childrenOf, personasOfCargo, level = 0 }) {
  const personas = personasOfCargo(cargo.id);
  const hijos = childrenOf(cargo.id);
  return (
    <div className="relative" style={{ marginLeft: level === 0 ? 0 : 24 }}>
      <div className="bg-white border-2 border-teal-200 rounded-xl p-3 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-teal-600 text-white text-xs font-bold">
              N{cargo.nivel}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800 truncate">{cargo.nombre}</p>
              {personas.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {personas.map(p => (
                    <span key={p.id} className="inline-flex items-center gap-1 bg-teal-50 text-teal-800 text-[11px] px-2 py-0.5 rounded-full font-medium">
                      <User className="w-2.5 h-2.5" /> {p.nombre} {p.apellido}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 mt-0.5">Sin persona asignada</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {hijos.length > 0 && (
        <div className="mt-2 space-y-2 border-l-2 border-teal-200 pl-3 ml-4">
          {hijos.map(h => (
            <CargoNode key={h.id} cargo={h} childrenOf={childrenOf} personasOfCargo={personasOfCargo} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

